/**
 * Sample runtime context for document template previews and unit tests.
 *
 * Hand-rolled fixture that populates every dotted path enumerated in
 * `documentTemplatePlaceholders.ts`. Used by:
 * - Task 6.3 — a catalog/fixture consistency test that walks every path
 *   in the catalog and asserts it resolves to a non-null scalar on this
 *   fixture.
 * - Task 8 — the admin template editor's live preview, which feeds this
 *   fixture through the same Mustache-style renderer as the production
 *   PDF generators so authors see realistic output without needing a
 *   real campaign + match in DDB.
 *
 * Conventions:
 * - Display strings are Korean (primary user language).
 * - Dates use ISO-8601 day precision (`YYYY-MM-DD`); timestamps use
 *   full ISO-8601 (`YYYY-MM-DDTHH:mm:ssZ`).
 * - Monetary amounts are integers denominated in KRW.
 * - This file intentionally does NOT import from the generated DDB /
 *   GraphQL types — keeping it dependency-free means the fixture stays
 *   stable even when those schemas evolve, and the catalog test
 *   remains the single source of truth for which paths must resolve.
 */

export const DOCUMENT_TEMPLATE_SAMPLE_FIXTURE = {
  campaign: {
    // 1. Basic info
    brandName: '샘플 브랜드',
    campaignName: '샘플 캠페인 - 봄맞이 신제품 체험단',
    campaignCategory: '식품/음료',
    clientBrandName: '샘플 클라이언트 브랜드',

    // 2. Product / service info
    productIntroduction: '신선한 국내산 원료로 만든 프리미엄 샘플 제품입니다.',
    productName: '샘플 프리미엄 세트',
    providedValueDescription: '정가 120,000원 상당의 제품 풀세트',
    provisionMethod: '택배 발송',
    visitAddress: '서울특별시 강남구 테헤란로 123',
    additionalNotes: '방문 48시간 전 담당자에게 연락 부탁드립니다.',

    // 3. Mission
    numberOfRecruits: 10,
    contentChannelsDisplay: '인스타그램, 네이버 블로그',
    rewardAmount: 500000,
    influencerPayoutAmount: 400000,
    influencerRewardAmount: 400000,
    publishingConditionsSummary: '인스타그램 피드 1건, 네이버 블로그 포스트 1건',
    contentInsertLink: 'https://example.com/sample-campaign',
    missionsSummary: '제품 체험 후 인스타그램 피드 1건과 블로그 포스트 1건 발행',

    // 4. Scheduling
    recruitmentPeriod: {
      startDate: '2024-05-01',
      endDate: '2024-05-15',
    },
    announcementStartDate: '2024-05-16',
    announcementEndDate: '2024-05-20',
    visitStartDate: '2024-06-01',
    visitEndDate: '2024-06-15',
    uploadDeadlineDays: 14,

    // 5. Content guide & secondary submissions
    contentWritingRequirements:
      '제품 사용 장면을 포함한 고해상도 사진 5장 이상과 300자 이상의 후기를 작성해 주세요.',
    secondarySubmissionsSummary: '원본 이미지 5장, 세로형 숏폼 영상 1편',
    submissionPath: 'https://example.com/submissions',
    secondarySubmissionGuide:
      '제출 경로에 원본 파일을 업로드하고 파일명에 인플루언서 이름을 포함해 주세요.',

    // 6. Document metadata
    proposalDate: '2024-04-20',
    proposalGeneratedAt: '2024-04-20T09:30:00Z',
  },

  match: {
    visitStartDate: '2024-06-03',
    visitEndDate: '2024-06-05',
    requestedAt: '2024-05-18T10:15:00Z',
    respondedAt: '2024-05-19T14:42:00Z',
  },

  influencer: {
    name: '홍길동',
  },

  host: {
    name: '샘플 호스트',
  },

  brand: {
    name: '샘플 브랜드',
  },
} as const;
